<template>
<!-- HISTORY:
   V260526.1: Made maxDailyScheduleHours disabled for all but the Power Admin.
   V260420.1: Added maxDailyScheduleHours dropdown under ETLs tab.
   V260323.1: Added maxDashboardMetrics and maxChartMetrics (for power admins) to limit metrics count in dashboards.
   V260320.1: 1st release (copied from CsSettings.vue to be adapted for Lake Services).
-->   
<!-- TODO: -->
<v-container fluid class="px-3 py-3">
   <v-card>
      <v-card-title class="pl-2 pb-2">
         <h1 class="title font-weight-bold grey--text darken-4 pl-2" style="color:#757575 !important">
            <v-icon class="pr-1">settings</v-icon>
            <span>Settings</span>
         </h1>
      </v-card-title>

      <v-card-text class="pb-2">
         <v-tabs dark
            class="elevation-2"
            background-color="grey lighten-2 accent-4"
            slider-color="black"
            v-model="tab"
         >
            <v-tab
               :class="isFormDashboardsValid ? 'black--text' : 'red--text'"
            >Dashboards</v-tab>
            <v-tab
               class="black--text"
            >ETLs</v-tab>
            <v-tab v-show="false"
            >Folders</v-tab>
            <v-tab v-show="false"
            >AI</v-tab>

            <v-tabs-items v-model="tab">
               <v-tab-item><!-- Dashboards tab -->
                  <v-card flat tile>
                     <v-card-text class="pt-2 pb-0">
                        <v-form lazy-validation
                           ref="formDashboards"
                           v-model="isFormDashboardsValid"
                        >
                           <v-row class="py-0">
                              <v-col xs="12" :md="isPowerAdmin ? 4: 12">
                                 <v-select dense persistent-hint hide-selected
                                    ref="timezone"
                                    hint="Timezone"
                                    placeholder="select a timezone..."
                                    :disabled="!canCreateSettings && !canEditSettings"
                                    :items="timezoneItems"
                                    :rules="[rules.required]"
                                    v-model="formData.dashboards.timezone"
                                    @change="settingsChanged"
                                 ></v-select>
                              </v-col>
                              <v-col xs="12" md="4" v-if="isPowerAdmin">
                                 <v-text-field dense persistent-hint
                                    class="pt-0 pb-2"
                                    ref="maxDashboardMetrics"
                                    autocomplete="off"
                                    hint="Max Dashboard Metrics"
                                    :placeholder="`enter a number between 1 to ${MAX_DASHBOARD_METRICS}`"
                                    :rules="[rules.required, rules.digitsOnly, rules.maxNumber(MAX_DASHBOARD_METRICS)]"
                                    inputmode="numeric"
                                    v-model="formData.dashboards.maxDashboardMetrics"
                                    @change="settingsChanged"
                                 ></v-text-field>
                              </v-col>
                              <v-col xs="12" md="4" v-if="isPowerAdmin">
                                 <v-text-field dense persistent-hint
                                    class="pt-0 pb-2"
                                    ref="maxChartMetrics"
                                    autocomplete="off"
                                    hint="Max Chart Metrics"
                                    :placeholder="`enter a number between 1 to ${MAX_CHART_METRICS}`"
                                    :rules="[rules.required, rules.digitsOnly, rules.maxNumber(MAX_CHART_METRICS)]"
                                    inputmode="numeric"
                                    v-model="formData.dashboards.maxChartMetrics"
                                    @change="settingsChanged"
                                 ></v-text-field>
                              </v-col>
                           </v-row>
                           <v-row class="pt-0">
                              <v-col cols="12">
                                 <v-textarea clearable outlined
                                    ref="labels"
                                    rows="4"
                                    label="Dashboards Labels in the JSON Array Format"
                                    :placeholder="labelsPlaceholder"
                                    :disabled="!canCreateSettings && !canEditSettings"
                                    :rules="[rules.validJsonArray,rules.duplicate]"
                                    v-model="formData.dashboards.labels"
                                    @keyup="settingsChanged"
                                    @click:clear="settingsChanged"
                                 ></v-textarea>
                              </v-col>
                           </v-row>
                        </v-form>
                     </v-card-text>
                  </v-card>
               </v-tab-item>

               <v-tab-item><!-- ETLs tab: V260420 -->
                  <v-card flat tile>
                     <v-card-text>
                        <v-row>
                           <v-col xs="12" md="2">
                              <!-- V260526: Added disabled prop -->
                              <v-select dense persistent-hint
                                 hint="* Max Daily Schedule Hours"
                                 :disabled="!canCreateSettings && !canEditSettings"
                                 :items="maxHours"
                                 v-model="formData.etls.maxDailyScheduleHours"
                                 @change="settingsChanged"
                              ></v-select>
                           </v-col>
                        </v-row>
                     </v-card-text>
                  </v-card>
               </v-tab-item>

               <v-tab-item v-show="false"><!-- Folders tab -->
               </v-tab-item>

               <v-tab-item v-show="false"><!-- AI tab -->
               </v-tab-item>

            </v-tabs-items>
         </v-tabs>
      </v-card-text>

      <v-card-actions class="py-0 pr-1">
         <div class="flex-grow-1"></div>
         <v-btn text
            class="px-0"
            color="blue darken-1"
            :disabled="!isSettingsChanged"
            @click="cancelSettings"
         >Cancel</v-btn>
         <v-btn text
            class="px-0"
            color="blue darken-1"
            :disabled="!isSettingsValid"
            @click="saveSettings"
         >Save</v-btn>
      </v-card-actions>
   </v-card>

</v-container>
</template>

<script>
import { APIService } from '../services/ls-api-service.js';
import { deepClone } from '../mixins/bt-mixin.js';

const NAME = "LsSettings";
const TABS = { dashboards: 0 };
const MAX_DASHBOARD_METRICS = 29;
const MAX_CHART_METRICS = 19;

class FormData {
   constructor(initData) {
      /*
      "databaseName": "19375",
      "dashboards" : {
         "timezone": "local | UTC",
         "labels": [
            { "id": 20102, "label": "Email sent to student" }
         ]
      },
      "etls": {},
      "folders": {},
      "ai": {}
      */

      this.databaseName = initData.databaseName || '';
      const dashboards = initData.dashboards || {};
      this.dashboards = {
         timezone: dashboards.timezone || '',
         maxDashboardMetrics: dashboards.maxDashboardMetrics || '',
         maxChartMetrics: dashboards.maxChartMetrics || '',
         labels: dashboards.labels ? JSON.stringify(dashboards.labels) : ''
      };

      // V260420
      const etls = initData.etls || {};
      this.etls = {
         maxDailyScheduleHours: etls.maxDailyScheduleHours || 1
      };
   }
}

export default {
   name: NAME,

   components: {},

   props: {
      debug: {
         type: Boolean,
         default: false
      },

      isActualEndpoint: {
         type: Boolean,
         default: true
      }
   },

   data() {
      return {
         MAX_DASHBOARD_METRICS,
         MAX_CHART_METRICS,
         tab: null,
         apiService: null,
         formData: new FormData({}),
         settings: {},
         rules: {
            required: value => !!value || "Value is required!",
            digitsOnly: value => {
               if (!value) return true;
               const str = String(value);
               if (!/^[1-9][0-9]*$/.test(str)) {
                  return `Must start with 1 and contain digits only`;
               }
               return true;
            },
            maxNumber: max => value => Number(value) <= max || `Must be ≤ ${max}`,
            validJsonArray: value => {
               try {
                  let json;
                  if (value) {
                     json = JSON.parse(value);
                     if (Array.isArray(json)) {
                        if (json.length) {
                           const invalids = [];
                           let noVal;
                           json.forEach((element, i) => {
                              const keys = Object.keys(element);
                              if (keys.length != 2 || keys[0] != 'id' || keys[1] != 'label')
                                 invalids.push(i + 1);
                              else if (!element.id?.trim()) noVal = 'id';
                              else if (!element.label?.trim()) noVal = 'label';
                           });
                           if (invalids.length) return `Items must only have "id" and "label" properties: ${invalids.join(', ')}!`;
                           else if (noVal) return `${noVal} cannot be empty!`;
                           else return true;
                        } else 
                           return 'The array should have one or more items!';
                     } else
                        return 'Should be an array!';
                  } else return true;
               } catch (error) {
                  return error.toString();
               }
            },
            duplicate: value => {
               if (!value || value.trim() === '') return true;

               let arr;
               try {
                  arr = JSON.parse(value);
               } catch (error) {
                  return true;
               }

               const ids = new Set();
               const labels = new Set();

               for (const item of arr) {
                  if (item.id?.toLowerCase()) {
                     if (ids.has(item.id.toLowerCase()))
                        return `Duplicate id found: ${item.id}`;
                     else ids.add(item.id.trim().toLowerCase());
                  }

                  if (item.label?.toLowerCase()) {
                     if (labels.has(item.label.toLowerCase()))
                        return `Duplicate label found: ${item.label}`;
                     else labels.add(item.label.trim().toLowerCase());
                  }
               }

               return true;
            }
         },
         loading: false,
         timezoneItems: [
            { text: 'Local', value: 'local' },
            { text: 'UTC', value: 'UTC' }
         ],
         isSettingsChanged: false,
         isFormDashboardsValid: true,
         labelsPlaceholder: 'example: [{"id":"20102", "label":"Email Sent"}]',
         isFormETLsValid: true,
         isFormFoldersValid: true,
         isFormAiValid: true,
         // V260420
         maxHours: Array.from({ length: 12 }, (_, i) => i + 1)
      }
   },

   computed: {
      token() {
         return this.$store.getters.token;
      },

      isPowerAdmin() {
         return this.$store.getters.user?.isPowerAdmin;
      },

      canCreateSettings() {
         // return this.$store.getters.user.policies.includes('contact-ls-settings-create');
         //V260323: return this.$store.getters.user.isPowerAdmin;
         return this.isPowerAdmin;
      },

      canEditSettings() {
         // return this.$store.getters.user.policies.includes('contact-ls-settings-update');
         //V260323: return this.$store.getters.user.isPowerAdmin;
         return this.isPowerAdmin;
      },

      isSettingsValid() {
         return this.isFormDashboardsValid &&
            this.isFormETLsValid &&
            this.isFormFoldersValid &&
            this.isFormAiValid &&
            this.isSettingsChanged;
      }
   },

   watch: {
      token() {
         this.init();
      },

      tab: {
         handler (val) {
            if (val === TABS.dashboards) {
               setTimeout(() => {
                  this.$refs.labels?.focus();
               }, 10);
            }
         }
      }
   },

   methods: {
      log(msg) {
         if (this.debug)
            console.log(`-----${NAME} V260526.1 says => ${msg}`);
      },

      logout() {
         this.$router.push('/');
      },

      async init() {
         try {
            this.settings = {};
            if (this.token) {
               this.apiService = new APIService(null, this.token, this.debug, this.isActualEndpoint);
               await this.getSettings();
               this.formData = new FormData(this.settings);
               if (!this.formData.dashboards.maxDashboardMetrics) {
                  this.formData.dashboards.maxDashboardMetrics = 15;
                  this.formData.dashboards.maxChartMetrics = 10;
                  this.settingsChanged();
               }
            }
         } catch (error) {
            alert(`Exception in ${NAME}.init(): ${error.message}`);
         }
      },

      async getSettings() {
         let result = await this.apiService.getSettings();
         if (result.logout)
            this.logout();
         else
            this.settings = result.data;
      },

      settingsChanged() {
         this.isSettingsChanged = true;
      },

      cancelSettings() {
         this.formData = new FormData(this.settings);
         this.isSettingsChanged = false;
      },

      async saveSettings() {
         this.loading = true;
         this.log('in saveSettings(): formData=' + JSON.stringify(this.formData));
         const { timezone, maxDashboardMetrics, maxChartMetrics, labels } = this.formData.dashboards;
         this.settings.dashboards = {
            timezone,
            maxDashboardMetrics: Number(maxDashboardMetrics),
            maxChartMetrics: Number(maxChartMetrics)
         };

         if (labels) {
            this.settings.dashboards.labels = JSON.parse(labels);
         }

         // V260420
         this.settings.etls = deepClone(this.formData.etls);

         let result;
         if (this.formData.databaseName)
            result = await this.apiService.updateSettings(this.settings);
         else
            result = await this.apiService.createSettings(this.settings);

         if (result.logout)
            this.logout();
         else if (!result.message) {
            this.$emit('snackbar-event', `Settings were saved.`);
            this.cancelSettings();
            this.isSettingsChanged = false;
         }
         this.loading = false;
      }
   },

   created() {
      this.init();
   },

   beforeRouteLeave (to, from, next) {
      if (!this.isSettingsChanged || window.confirm('Do you really want to leave? you have unsaved changes!'))
         next();
      else
         next(false);
   }
}
</script>

<style scoped>
</style>